import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Snackbar,
  Avatar,
  Divider,
  useTheme,
  alpha,
  Collapse,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  VideoCall as VideoCallIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  MedicalServices as MedicalServicesIcon,
  EventNote as EventNoteIcon,
  MedicalServicesOutlined,
  FolderOpen as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  NoteAdd as NoteAddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { confirmAppointment, completeAppointment, fetchHealthDocuments, startVideoCall } from '../../services/healinApi';
import { useAuth } from '../../auth/AuthContext';
import TwilioVideo from '../TwilioVideo/TwilioVideo';

function AppointmentDetail({ 
  appointment, 
  onBack, 
  onAppointmentUpdate,
  onStartVideoCall 
}) {
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [healthDocuments, setHealthDocuments] = useState(null);
  const [healthDocumentsLoading, setHealthDocumentsLoading] = useState(false);
  const [healthDocumentsError, setHealthDocumentsError] = useState(null);
  const [showHealthDocuments, setShowHealthDocuments] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);

  if (!appointment) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        No appointment data available.
      </Alert>
    );
  }

  // Current doctor info from authentication
  const currentDoctor = {
    id: user?.username || 'unknown-doctor',
    name: user?.attributes?.name || user?.name || user?.username || 'Doctor',
    specialty: user?.attributes?.['custom:specialty'] || 'Healthcare Provider',
    email: user?.attributes?.email || user?.email || '',
    phone: user?.attributes?.phone_number || user?.phone || ''
  };

  // Get status info for display
  const getStatusInfo = () => {
    const status = appointment.status?.toUpperCase();
    switch (status) {
      case 'SCHEDULED':
        return { 
          label: 'Scheduled', 
          color: 'warning',
          bgColor: alpha(theme.palette.warning.main, 0.1),
          textColor: theme.palette.warning.main
        };
      case 'CONFIRMED':
        return { 
          label: 'Confirmed', 
          color: 'success',
          bgColor: alpha(theme.palette.success.main, 0.1),
          textColor: theme.palette.success.main
        };
      case 'ONGOING':
        return { 
          label: 'Ongoing', 
          color: 'primary',
          bgColor: alpha(theme.palette.primary.main, 0.1),
          textColor: theme.palette.primary.main
        };
      case 'COMPLETED':
        return { 
          label: 'Completed', 
          color: 'info',
          bgColor: alpha(theme.palette.info.main, 0.1),
          textColor: theme.palette.info.main
        };
      case 'CANCELLED':
        return { 
          label: 'Cancelled', 
          color: 'error',
          bgColor: alpha(theme.palette.error.main, 0.1),
          textColor: theme.palette.error.main
        };
      default:
        return { 
          label: status || 'Unknown', 
          color: 'default',
          bgColor: alpha(theme.palette.grey[500], 0.1),
          textColor: theme.palette.grey[600]
        };
    }
  };

  const statusInfo = getStatusInfo();
  const isScheduledStatus = appointment.status?.toUpperCase() === 'SCHEDULED';
  const isConfirmedStatus = appointment.status?.toUpperCase() === 'CONFIRMED';
  const isOngoingStatus = appointment.status?.toUpperCase() === 'ONGOING';
  const isCompletedStatus = appointment.status?.toUpperCase() === 'COMPLETED';

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle appointment confirmation
  const handleConfirmAppointment = async () => {
    try {
      setLoading(true);
      
      // Confirm the appointment with a slot (using current time + 1 hour as example)
      const confirmedSlot = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      
      await confirmAppointment({
        userId: appointment.userId,
        requestId: appointment.requestId,
        assignedDoctorId: currentDoctor.id,
        confirmedSlot: confirmedSlot
      });

      setSnackbar({
        open: true,
        message: 'Appointment confirmed successfully!',
        severity: 'success'
      });

      setConfirmDialogOpen(false);
      
      // Notify parent component of the update
      if (onAppointmentUpdate) {
        onAppointmentUpdate();
      }
    } catch (error) {
      console.error('Error confirming appointment:', error);
      setSnackbar({
        open: true,
        message: `Failed to confirm appointment: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle appointment completion
  const handleCompleteAppointment = async () => {
    try {
      setLoading(true);
      
      await completeAppointment({
        userId: appointment.userId,
        requestId: appointment.requestId,
        doctorComments: 'Consultation completed successfully'
      });

      setSnackbar({
        open: true,
        message: 'Appointment marked as completed!',
        severity: 'success'
      });

      setCompleteDialogOpen(false);
      
      // Notify parent component of the update
      if (onAppointmentUpdate) {
        onAppointmentUpdate();
      }
    } catch (error) {
      console.error('Error completing appointment:', error);
      setSnackbar({
        open: true,
        message: `Failed to complete appointment: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle video call start
  const handleStartVideoCall = async () => {
    try {
      setLoading(true);
      
      // For confirmed appointments, start the video call via API first
      // For ongoing appointments, just join the existing call
      if (isConfirmedStatus) {
        console.log('Starting new video call for appointment:', appointment.requestId);
        await startVideoCall({
          userId: appointment.userId,
          requestId: appointment.requestId,
          assignedDoctorId: currentDoctor.id
        });
        
        setSnackbar({
          open: true,
          message: 'Video call started successfully!',
          severity: 'success'
        });
      } else if (isOngoingStatus) {
        console.log('Joining existing video call for appointment:', appointment.requestId);
        setSnackbar({
          open: true,
          message: 'Joining video call...',
          severity: 'info'
        });
      }
      
      // Show the video call interface
      setShowVideoCall(true);
      
      // Notify parent component of the update
      if (onAppointmentUpdate) {
        onAppointmentUpdate();
      }
      
    } catch (error) {
      console.error('Error with video call:', error);
      let errorMessage = 'Failed to start video call';
      
      if (error.message.includes('Room not found')) {
        errorMessage = 'Video room not found. The call may have expired. Please try again.';
      } else if (error.message.includes('already exists')) {
        // If room already exists, just join it
        console.log('Room already exists, joining existing call');
        setShowVideoCall(true);
        setSnackbar({
          open: true,
          message: 'Joining existing video call...',
          severity: 'info'
        });
        return;
      } else {
        errorMessage = `Failed to start video call: ${error.message}`;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle video call end
  const handleEndVideoCall = () => {
    setShowVideoCall(false);
  };

  // Handle health documents fetch
  const handleCheckHealthDocuments = async () => {
    if (!appointment?.userId || !appointment?.planDetail?.profileID) {
      setSnackbar({
        open: true,
        message: 'Unable to fetch health documents: missing user or profile information',
        severity: 'error'
      });
      return;
    }

    setHealthDocumentsLoading(true);
    setHealthDocumentsError(null);
    
    try {
      console.log('Fetching health documents for:', {
        userId: appointment.userId,
        identityId: appointment.planDetail.profileID
      });
      
      const documentsData = await fetchHealthDocuments(appointment.userId, appointment.planDetail.profileID);
      setHealthDocuments(documentsData);
      setShowHealthDocuments(true);
      setSnackbar({
        open: true,
        message: 'Health documents loaded successfully',
        severity: 'success'
      });
      console.log('Health documents loaded:', documentsData);
    } catch (error) {
      console.error('Error fetching health documents:', error);
      setHealthDocumentsError(error.message);
      setSnackbar({
        open: true,
        message: `Error loading health documents: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setHealthDocumentsLoading(false);
    }
  };

  // Handle notes save
  const handleSaveNotes = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, you would save the notes to the appointment record
      // For now, we'll just simulate a successful save
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setNotesSaved(true);
      setSnackbar({
        open: true,
        message: 'Notes saved successfully',
        severity: 'success'
      });
      
      // Auto-hide the saved indicator after 3 seconds
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (error) {
      console.error('Error saving notes:', error);
      setSnackbar({
        open: true,
        message: `Error saving notes: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (showVideoCall) {
    return (
      <TwilioVideo 
        appointment={appointment}
        onEndCall={handleEndVideoCall}
        doctorName={currentDoctor.name}
        autoStart={true}
      />
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: statusInfo.bgColor }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={onBack} 
            sx={{ mr: 2, bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Appointment Details
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {appointment.patientName || `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`}
            </Typography>
          </Box>
          <Chip 
            label={statusInfo.label}
            color={statusInfo.color}
            size="large"
            sx={{ 
              fontWeight: 'bold',
              fontSize: '1rem',
              height: '40px'
            }}
          />
        </Box>

        {/* Quick Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {isScheduledStatus && (
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<CheckCircleIcon />}
              onClick={() => setConfirmDialogOpen(true)}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Confirm Appointment
            </Button>
          )}
          
          {isConfirmedStatus && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<VideoCallIcon />}
                onClick={handleStartVideoCall}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Start Video Call
              </Button>
              <Button
                variant="outlined"
                color="info"
                size="large"
                startIcon={<AssignmentIcon />}
                onClick={() => setCompleteDialogOpen(true)}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Mark Complete
              </Button>
            </>
          )}

          {isOngoingStatus && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<VideoCallIcon />}
                onClick={handleStartVideoCall}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Join Video Call
              </Button>
              <Button
                variant="outlined"
                color="info"
                size="large"
                startIcon={<AssignmentIcon />}
                onClick={() => setCompleteDialogOpen(true)}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Mark Complete
              </Button>
            </>
          )}

          {isCompletedStatus && (
            <Chip 
              label="Appointment Completed" 
              color="info" 
              variant="outlined"
              size="large"
              sx={{ 
                fontWeight: 'bold',
                fontSize: '1rem',
                height: '40px'
              }}
            />
          )}
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column - Appointment Information */}
        <Grid item xs={12} lg={6}>
          {/* Appointment Details Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                <EventNoteIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                Appointment Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      Request ID
                    </Typography>
                    <Typography variant="body1">
                      {appointment.requestId || 'Not specified'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      Type
                    </Typography>
                    <Typography variant="body1">
                      {appointment.appointmentType || 'Video Consultation'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      Duration
                    </Typography>
                    <Typography variant="body1">
                      {appointment.duration || '30'} minutes
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      Requested Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(appointment.requestedAt || appointment.createdAt)}
                    </Typography>
                  </Box>
                </Grid>

                {appointment.confirmedSlot && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Confirmed Date
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(appointment.confirmedSlot)}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Confirmed Time
                        </Typography>
                        <Typography variant="body1">
                          {formatTime(appointment.confirmedSlot)}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}

                {appointment.completedAt && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        Completed At
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(appointment.completedAt)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Comments Card */}
          {(appointment.userComments || appointment.doctorComments) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <AssignmentIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                  Comments
                </Typography>
                
                {appointment.userComments && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Patient Comments:
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {appointment.userComments}
                    </Typography>
                  </Box>
                )}
                
                {appointment.doctorComments && (
                  <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Doctor Comments:
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {appointment.doctorComments}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Patient Information */}
        <Grid item xs={12} lg={6}>
          {/* Patient Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
                Patient Information
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 64, 
                    height: 64, 
                    bgcolor: theme.palette.secondary.main,
                    mr: 2,
                    fontSize: '1.5rem'
                  }}
                >
                  {appointment.patientName ? 
                    appointment.patientName.split(' ').map(n => n[0]).join('').toUpperCase() :
                    'P'
                  }
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {appointment.patientName || `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    User ID: {appointment.userId || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {appointment.patientInfo?.age && (
                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        Age
                      </Typography>
                      <Typography variant="body1">
                        {appointment.patientInfo.age} years
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {appointment.patientInfo?.gender && (
                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        Gender
                      </Typography>
                      <Typography variant="body1">
                        {appointment.patientInfo.gender}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {appointment.patientInfo?.email && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <EmailIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {appointment.patientInfo.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {appointment.patientInfo?.phone && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Phone Number
                        </Typography>
                        <Typography variant="body1">
                          {appointment.patientInfo.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {appointment.patientInfo?.address && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {appointment.patientInfo.address}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Medical Information */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                <MedicalServicesIcon sx={{ mr: 1, color: theme.palette.error.main }} />
                Medical Information
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Chronic Conditions:
              </Typography>
              {/* Show conditions from Plan of Care first, otherwise fallback to patientInfo */}
              {(() => {
                // Try to get conditions from Plan of Care first
                let conditions = [];
                
                if (appointment.planDetail && appointment.planDetail.Pathways) {
                  try {
                    const pathways = JSON.parse(appointment.planDetail.Pathways);
                    conditions = pathways.map(pathway => ({
                      name: pathway.patologia_de_base,
                      icd10: pathway.ICD10,
                      id: pathway.ID_Pathway
                    }));
                  } catch (error) {
                    console.error('Error parsing pathways:', error);
                  }
                }
                
                // Fallback to patientInfo conditions if no pathways found
                if (conditions.length === 0 && appointment.patientInfo?.chronicConditions) {
                  conditions = appointment.patientInfo.chronicConditions.map(condition => ({
                    name: condition
                  }));
                }
                
                return conditions.length > 0 ? (
                  <Box sx={{ mt: 0, mb: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {conditions.map((condition, index) => (
                        <Chip 
                          key={index}
                          label={condition.icd10 ? `${condition.name} (${condition.icd10})` : condition.name}
                          color="secondary" 
                          variant="outlined"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Box>
                    {conditions.some(c => c.icd10) && appointment.planDetail && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Conditions shown with ICD-10 codes from Plan of Care
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No chronic conditions recorded
                  </Typography>
                );
              })()}

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Allergies:
              </Typography>
              {appointment.patientInfo?.allergies && appointment.patientInfo.allergies.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {appointment.patientInfo.allergies.map((allergy, index) => (
                    <Chip 
                      key={index}
                      label={allergy} 
                      size="small" 
                      variant="outlined"
                      color="error"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No allergies recorded
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Plan of Care Information Card */}
          {appointment.planDetail && (
            <Card sx={{ mb: 3 }}>
              <CardHeader
                avatar={<MedicalServicesOutlined color="primary" />}
                title="Plan of Care Information"
                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                
                <Grid container spacing={2}>
                  {appointment.planDetail.PlanName && (
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Plan Name
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                          {appointment.planDetail.PlanName}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {appointment.planDetail.Description && (
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Description
                        </Typography>
                        <Typography variant="body1">
                          {appointment.planDetail.Description}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid container spacing={2}>
                    {appointment.planDetail.PlanType && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Plan Type
                          </Typography>
                          <Typography variant="body1">
                            {appointment.planDetail.PlanType}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {appointment.planDetail.Status && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Plan Status
                          </Typography>
                          <Chip 
                            label={appointment.planDetail.Status} 
                            color={appointment.planDetail.Status === 'ACTIVE' ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      </Grid>
                    )}

                    {appointment.planDetail.StartDate && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Start Date
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(appointment.planDetail.StartDate)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {appointment.planDetail.EndDate && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            End Date
                        </Typography>
                          <Typography variant="body1">
                            {formatDate(appointment.planDetail.EndDate)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>



                  {/* Goals and Objectives */}
                  {appointment.planDetail.Goals && appointment.planDetail.Goals.length > 0 && (
                    <>
                      <Divider sx={{ my: 2, width: '100%' }} />
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                          Plan Goals
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {appointment.planDetail.Goals.map((goal, index) => (
                            <Chip 
                              key={index}
                              label={goal} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Grid>
                    </>
                  )}

                  {/* Care Team */}
                  {appointment.planDetail.CareTeam && appointment.planDetail.CareTeam.length > 0 && (
                    <>
                      <Divider sx={{ my: 2, width: '100%' }} />
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                          Care Team
                        </Typography>
                        {appointment.planDetail.CareTeam.map((member, index) => (
                          <Box key={index} sx={{ mb: 1, p: 1, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {member.Name || member.Role || `Team Member ${index + 1}`}
                            </Typography>
                            {member.Role && member.Name && (
                              <Typography variant="body2" color="text.secondary">
                                {member.Role}
                              </Typography>
                            )}
                            {member.Contact && (
                              <Typography variant="body2" color="text.secondary">
                                {member.Contact}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Grid>
                    </>
                  )}



                  {/* Health Documents Button */}
                  {appointment.planDetail.profileID && (
                    <>
                      <Divider sx={{ my: 2, width: '100%' }} />
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          startIcon={healthDocumentsLoading ? <CircularProgress size={16} /> : <FolderIcon />}
                          onClick={handleCheckHealthDocuments}
                          disabled={healthDocumentsLoading}
                          size="small"
                          fullWidth
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500
                          }}
                        >
                          {healthDocumentsLoading ? 'Loading...' : 'Check Health Documents'}
                        </Button>
                      </Grid>
                    </>
                  )}


                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Health Documents Section */}
          {(showHealthDocuments || healthDocuments) && (
            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <FolderIcon />
                  </Avatar>
                }
                title="Health Documents"
                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                subheader={`Documents for ${appointment.patientName || 'Patient'}`}
                action={
                  <IconButton onClick={() => setShowHealthDocuments(!showHealthDocuments)}>
                    {showHealthDocuments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                }
              />
              <Collapse in={showHealthDocuments}>
                <CardContent>
                {healthDocumentsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {healthDocumentsError}
                  </Alert>
                )}
                
                {healthDocuments && healthDocuments.files ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Found {healthDocuments.files.length} document{healthDocuments.files.length !== 1 ? 's' : ''} 
                      {healthDocuments.files.length > 5 && ' (scroll to see more)'}
                    </Typography>
                    
                    {/* Scrollable container for documents */}
                    <Box
                      sx={{
                        maxHeight: '400px', // Height for approximately 5 documents
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'grey.100',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'grey.400',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: 'grey.600',
                          },
                        },
                      }}
                    >
                      <List sx={{ py: 0 }}>
                        {healthDocuments.files.map((file, index) => {
                          const getFileIcon = (fileType) => {
                            const type = fileType?.toLowerCase();
                            if (type === 'pdf') return <PdfIcon color="error" />;
                            if (['jpg', 'jpeg', 'png', 'gif'].includes(type)) return <ImageIcon color="primary" />;
                            if (['doc', 'docx', 'txt'].includes(type)) return <DescriptionIcon color="info" />;
                            return <FileIcon />;
                          };

                          return (
                            <ListItem 
                              key={index} 
                              divider={index < healthDocuments.files.length - 1}
                              sx={{ 
                                py: 1.5,
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: '40px' }}>
                                {getFileIcon(file.FileType)}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {file.FileName}
                                  </Typography>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      Size: {file.SizeKB} KB • Type: {file.FileType?.toUpperCase() || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Last Modified: {file.LastModified}
                                    </Typography>
                                  </Box>
                                }
                              />
                              <Button
                                component="a"
                                href={file.PreviewURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                sx={{ 
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  ml: 1,
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    borderColor: 'primary.main',
                                  }
                                }}
                              >
                                View
                              </Button>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                    
                    {/* Document count summary at bottom */}
                    {healthDocuments.files.length > 5 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                        Showing all {healthDocuments.files.length} documents • Scroll up to see more
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No documents available
                  </Typography>
                )}
                </CardContent>
              </Collapse>
            </Card>
          )}

          {/* Doctor Information Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                <MedicalServicesIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                Attending Doctor
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar 
                  sx={{ 
                    width: 48, 
                    height: 48, 
                    bgcolor: theme.palette.primary.main,
                    mr: 2
                  }}
                >
                  {currentDoctor.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {currentDoctor.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentDoctor.specialty}
                  </Typography>
                </Box>
              </Box>

              {currentDoctor.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ mr: 1, color: theme.palette.info.main, fontSize: 20 }} />
                  <Typography variant="body2">
                    {currentDoctor.email}
                  </Typography>
                </Box>
              )}

              {currentDoctor.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ mr: 1, color: theme.palette.info.main, fontSize: 20 }} />
                  <Typography variant="body2">
                    {currentDoctor.phone}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Clinical Notes and Prescription Software Section - Full Width Row */}
      <Grid container spacing={3} sx={{ mt: 3, mb: 3 }}>
        {/* Clinical Notes Section - 60% width */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                <NoteAddIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                Clinical Notes
              </Typography>
              
              <TextField
                fullWidth
                label="Doctor's Notes"
                multiline
                rows={12}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                variant="outlined"
                placeholder="Add clinical notes, observations, or instructions for the patient..."
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSaveNotes}
                  startIcon={notesSaved ? <CheckCircleIcon /> : <SaveIcon />}
                  disabled={loading || !notes.trim()}
                  sx={{ 
                    bgcolor: notesSaved ? 'success.main' : 'primary.main',
                    '&:hover': {
                      bgcolor: notesSaved ? 'success.dark' : 'primary.dark'
                    }
                  }}
                >
                  {notesSaved ? 'Saved' : 'Save Notes'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Certified Prescription Software Section - 40% width */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                <MedicalServicesOutlined sx={{ mr: 1, color: theme.palette.primary.main }} />
                Certified Prescription Software
              </Typography>
              
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2, 
                  borderRadius: 1,
                  '& .MuiAlert-message': {
                    fontSize: '0.75rem'
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, fontSize: '0.75rem' }}>
                  Patient: {appointment.patientName || `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Use the certified prescription software below to create official prescriptions.
                </Typography>
              </Alert>
              
              <Box sx={{ 
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
                height: '460px',
                bgcolor: 'background.paper'
              }}>
                <iframe
                  src="https://www.ccare.eu/icare/log_ent.home"
                  width="100%"
                  height="100%"
                  style={{ 
                    border: 'none',
                    display: 'block'
                  }}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                  title="Certified Prescription Software"
                  loading="lazy"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Appointment
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to confirm this appointment with {appointment.patientName || 'the patient'}?
            This will send a confirmation notification to the patient and schedule the appointment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAppointment} 
            variant="contained" 
            color="success"
            disabled={loading}
          >
            Confirm Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        aria-labelledby="complete-dialog-title"
      >
        <DialogTitle id="complete-dialog-title">
          Mark Appointment as Complete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark this appointment as completed?
            This action will finalize the appointment record and indicate that the consultation has been finished.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCompleteAppointment} 
            variant="contained" 
            color="info"
            disabled={loading}
          >
            Mark Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AppointmentDetail; 